import { useMemo, useState } from "react";
import { Check, Search, Sparkles, X } from "lucide-react";
import { Dialog, DialogContent } from "@/modern/components/ui/dialog-shadcn";
import { cn } from "@/modern/utils";
import type { SurfaceSolutionTag } from "../api/types";

type Props = {
  tags: SurfaceSolutionTag[];
  value: number[];
  onChange: (ids: number[]) => void;
  /** Prefill / AI candidate ids — stay visible as one-tap chips even when deselected. */
  suggestedIds?: number[];
};

const QUICK_PICK_LIMIT = 8;

export default function PromoteSurfaceTagPicker({
  tags,
  value,
  onChange,
  suggestedIds = [],
}: Props) {
  const [browseOpen, setBrowseOpen] = useState(false);

  const toggle = (id: number) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };

  const suggestedSet = useMemo(() => new Set(suggestedIds), [suggestedIds]);

  const suggestedTags = useMemo(
    () => tags.filter((t) => suggestedSet.has(t.id)),
    [tags, suggestedSet]
  );

  const selectedOnly = useMemo(
    () => tags.filter((t) => value.includes(t.id) && !suggestedSet.has(t.id)),
    [tags, value, suggestedSet]
  );

  /** Compact strip: suggested first, then selected extras, then a short quick-pick row. */
  const quickVisible = useMemo(() => {
    const seen = new Set<number>();
    const result: SurfaceSolutionTag[] = [];
    const push = (list: SurfaceSolutionTag[]) => {
      for (const t of list) {
        if (seen.has(t.id)) continue;
        seen.add(t.id);
        result.push(t);
      }
    };
    push(suggestedTags);
    push(selectedOnly);
    if (result.length < QUICK_PICK_LIMIT) {
      push(tags.slice(0, QUICK_PICK_LIMIT));
    }
    return result.slice(0, Math.max(QUICK_PICK_LIMIT, suggestedTags.length + selectedOnly.length));
  }, [suggestedTags, selectedOnly, tags]);

  const needsBrowse = tags.length > quickVisible.length;

  if (!tags.length) {
    return <p className="text-sm text-muted-foreground">No solution tags available.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Solution tags">
        {quickVisible.map((tag) => {
          const active = value.includes(tag.id);
          const suggested = suggestedSet.has(tag.id);
          return (
            <button
              key={tag.id}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(tag.id)}
              className={cn(
                "inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition",
                "active:scale-[0.98]",
                active
                  ? "border-primary/40 bg-primary text-primary-foreground"
                  : "border-border bg-muted/40 text-muted-foreground hover:border-foreground/30 hover:bg-muted hover:text-foreground"
              )}
            >
              {active && <Check className="h-3 w-3 shrink-0" strokeWidth={3} />}
              <span className="truncate">{tag.label}</span>
              {suggested && (
                <span
                  className={cn(
                    "inline-flex shrink-0 items-center gap-0.5 rounded px-1 py-px text-[10px] font-semibold",
                    active
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-violet-500/15 text-violet-500 dark:text-violet-300"
                  )}
                >
                  <Sparkles className="h-2 w-2" />
                  AI
                </span>
              )}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => setBrowseOpen(true)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md border border-dashed border-primary/40 bg-primary/5 px-2.5 py-1.5 text-xs font-medium text-primary transition-colors",
          "hover:border-primary/60 hover:bg-primary/10"
        )}
      >
        <Search className="h-3 w-3" />
        {needsBrowse ? `Browse all (${tags.length})` : "Search tags"}
      </button>

      <SolutionTagBrowseDialog
        open={browseOpen}
        onOpenChange={setBrowseOpen}
        tags={tags}
        value={value}
        onToggle={toggle}
        suggestedSet={suggestedSet}
      />
    </div>
  );
}

function SolutionTagBrowseDialog({
  open,
  onOpenChange,
  tags,
  value,
  onToggle,
  suggestedSet,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tags: SurfaceSolutionTag[];
  value: number[];
  onToggle: (id: number) => void;
  suggestedSet: Set<number>;
}) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter(
      (t) =>
        t.label.toLowerCase().includes(q) ||
        (t.tag_group ?? "").toLowerCase().includes(q)
    );
  }, [tags, search]);

  const groups = useMemo(() => {
    const map = new Map<string, SurfaceSolutionTag[]>();
    for (const tag of filtered) {
      const key = tag.tag_group?.trim() || "Other";
      const list = map.get(key);
      if (list) list.push(tag);
      else map.set(key, [tag]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="z-[60] gap-0 p-0 sm:max-w-lg"
        overlayClassName="z-[60]"
      >
        <div className="flex items-center justify-between gap-3 px-5 pb-3 pt-5">
          <h3 className="text-base font-semibold text-foreground">Solution tags</h3>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            title="Close"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or group…"
              autoFocus
              className="h-9 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          {value.length > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              {value.length} selected — toggle chips, then close when done.
            </p>
          )}
        </div>

        <div className="max-h-[50vh] space-y-4 overflow-y-auto px-5 pb-5 [scrollbar-width:thin]">
          {groups.map(([group, groupTags]) => (
            <div key={group} className="space-y-1.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {group}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {groupTags.map((tag) => {
                  const active = value.includes(tag.id);
                  const suggested = suggestedSet.has(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      aria-pressed={active}
                      onClick={() => onToggle(tag.id)}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition",
                        active
                          ? "border-primary/40 bg-primary text-primary-foreground"
                          : "border-border bg-transparent text-foreground hover:bg-muted"
                      )}
                    >
                      {active && <Check className="h-3 w-3" strokeWidth={3} />}
                      {tag.label}
                      {suggested && (
                        <span
                          className={cn(
                            "inline-flex items-center gap-0.5 rounded px-1 py-px text-[10px] font-semibold",
                            active
                              ? "bg-primary-foreground/20 text-primary-foreground"
                              : "bg-violet-500/15 text-violet-500 dark:text-violet-300"
                          )}
                        >
                          <Sparkles className="h-2 w-2" />
                          AI
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No tags match your search.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
